import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Users, X, CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { CTAButton } from "./primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SIZES = ["3–5 leaders", "6–10 leaders", "11–20 leaders", "20+ leaders"];

const inputCls =
  "w-full bg-white border border-hair rounded-sm px-4 py-3 text-sm text-navy placeholder:text-slate-ink/50 focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal transition-colors";

export const CohortEnquiry = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | loading | success
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    first_name: "", last_name: "", work_email: "", organisation: "",
    cohort_size: "", transform_goal: "",
  });

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => { setStatus("idle"); setErrors({}); }, 200);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Required";
    if (!form.last_name.trim()) e.last_name = "Required";
    if (!form.work_email.trim() || !EMAIL_RE.test(form.work_email)) e.work_email = "Enter a valid work email";
    if (!form.organisation.trim()) e.organisation = "Required";
    if (!form.cohort_size) e.cohort_size = "Please choose a team size";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        intent: "cohort",
        referring_url: window.location.href,
      });
      navigate("/prospectus/thank-you?type=cohort");
    } catch (err) {
      setErrors((e) => ({ ...e, submit: "Something went wrong. Please try again." }));
      setStatus("idle");
    }
  };

  return (
    <>
      <div
        data-testid="cohort-banner"
        className="mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 rounded-sm border border-navy bg-navy p-6 lg:p-8"
      >
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm bg-gold/15">
            <Users className="h-5 w-5 text-gold" />
          </div>
          <div>
            <p className="font-display font-bold text-white text-lg tracking-tight">Enrolling a leadership team?</p>
            <p className="mt-1 text-white/70 text-sm leading-relaxed max-w-xl">
              Cohort pricing and a tailored delivery plan are available for organisations enrolling a group.
              Ideal for MDs and executive teams driving transformation together.
            </p>
          </div>
        </div>
        <CTAButton
          variant="gold"
          data-testid="cohort-open"
          onClick={() => setOpen(true)}
          className="shrink-0"
          withArrow
        >
          Request cohort pricing
        </CTAButton>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="cohort-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            onClick={close}
          >
            <div className="absolute inset-0 bg-navy/80 backdrop-blur-sm" />
            <motion.div
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ duration: 0.25 }}
              role="dialog"
              aria-modal="true"
              aria-label="Request cohort pricing"
              className="relative w-full max-w-lg bg-offwhite rounded-sm border border-hair p-8 shadow-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="cohort-close"
                onClick={close}
                className="absolute top-4 right-4 text-slate-ink hover:text-navy"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>

              {status === "success" ? (
                <div className="text-center py-6" data-testid="cohort-success">
                  <CheckCircle2 className="h-12 w-12 text-teal mx-auto" />
                  <h3 className="mt-5 font-display font-bold text-navy text-2xl tracking-tight">
                    Thank you — we've got it.
                  </h3>
                  <p className="mt-3 text-slate-ink text-sm leading-relaxed max-w-sm mx-auto">
                    A member of our team will be in touch within 24 business hours to discuss cohort pricing
                    and a delivery plan tailored to your leadership team.
                  </p>
                  <CTAButton className="mt-8" data-testid="cohort-done" onClick={close}>Done</CTAButton>
                </div>
              ) : (
                <>
                  <p className="eyebrow text-gold">Team / Cohort enrolment</p>
                  <h3 className="mt-2 font-display font-extrabold text-navy text-xl tracking-tight">
                    Request cohort pricing
                  </h3>
                  <p className="mt-2 text-slate-ink text-sm leading-relaxed">
                    Tell us a little about your team and we'll prepare a tailored proposal.
                  </p>

                  <div className="mt-6 grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-display font-semibold text-navy mb-1.5">First name *</label>
                      <input data-testid="cohort-first-name" className={inputCls}
                        value={form.first_name} onChange={(e) => update("first_name", e.target.value)} />
                      {errors.first_name && <p className="mt-1 text-xs text-[#b91c1c]">{errors.first_name}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-display font-semibold text-navy mb-1.5">Last name *</label>
                      <input data-testid="cohort-last-name" className={inputCls}
                        value={form.last_name} onChange={(e) => update("last_name", e.target.value)} />
                      {errors.last_name && <p className="mt-1 text-xs text-[#b91c1c]">{errors.last_name}</p>}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-display font-semibold text-navy mb-1.5">Work email *</label>
                      <input type="email" data-testid="cohort-work-email" className={inputCls}
                        value={form.work_email} onChange={(e) => update("work_email", e.target.value)} />
                      {errors.work_email && <p className="mt-1 text-xs text-[#b91c1c]">{errors.work_email}</p>}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-display font-semibold text-navy mb-1.5">Organisation *</label>
                      <input data-testid="cohort-organisation" className={inputCls}
                        value={form.organisation} onChange={(e) => update("organisation", e.target.value)} />
                      {errors.organisation && <p className="mt-1 text-xs text-[#b91c1c]">{errors.organisation}</p>}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-display font-semibold text-navy mb-1.5">How many leaders? *</label>
                      <select data-testid="cohort-size" className={inputCls}
                        value={form.cohort_size} onChange={(e) => update("cohort_size", e.target.value)}>
                        <option value="">Select…</option>
                        {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      {errors.cohort_size && <p className="mt-1 text-xs text-[#b91c1c]">{errors.cohort_size}</p>}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-display font-semibold text-navy mb-1.5">What would you like the team to achieve?</label>
                      <textarea rows={3} data-testid="cohort-goal" className={inputCls}
                        value={form.transform_goal} onChange={(e) => update("transform_goal", e.target.value)} />
                    </div>
                  </div>

                  {errors.submit && <p className="mt-3 text-sm text-[#b91c1c]" data-testid="cohort-error">{errors.submit}</p>}

                  <CTAButton
                    className="mt-6 w-full"
                    data-testid="cohort-submit"
                    disabled={status === "loading"}
                    onClick={submit}
                  >
                    {status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send my enquiry <ArrowRight className="h-4 w-4" /></>}
                  </CTAButton>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
